import React, { useState } from "react";
import { $Button, $Input, $Popover, $Skeleton } from "us.common/components";
import { MoreOutlined, BoxIcons, IconTypes, EyeOutlined } from "us.icons";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import * as Actions from "actions";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import { ValidationSchema } from "./Validations";
import { MoreActionType } from "components/EditorHome/Constants";

/**
 * @description - Edit category and view category details
 * @link Design Document -
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 11/10/2023
 */
interface IMoreOption {
  category: any;
  categories: any;
}

const MoreOption: React.FC<PropsFromRedux & IMoreOption> = (props) => {
  const { t } = useTranslation();

  const {
    isDisplayNameUpdating,
    catgoryDetails,
    updateDisplayName,
    getCategoryDetails,
    category,
    categories,
  } = props;

  const { categoryId, displayName: categoryDisplayName  } = category ?? {}
  const { data, isLoading } = catgoryDetails ?? {};

  const [popVisible, setPopVisible] = useState<number>(-1);
  const [actionVisible, setActionVisible] = useState({
    isVisible: false,
    action: "",
  });

  /**
   * @description - View button click and get category details
   */
  const veiwCategoryDetails = () => {
    setActionVisible({ isVisible: true, action: MoreActionType.VIEW_DETAILS });
    getCategoryDetails && getCategoryDetails({ categoryId });
  };

  /**
   * @description - Update category display name action
   * @param values - Form values
   */
  const saveDisplayName = (values: any) => {
    if (values?.displayName?.length) {
      updateDisplayName &&
        updateDisplayName({
          categoryId,
          displayName: values.displayName,
        });
      setActionVisible({ isVisible: false, action: "" });
    }
  };

  /**
   * @description - Popover content
   * @param handleSubmit - Formik function
   * @returns
   */
  const content = (handleSubmit: any, restProps: any, values: any) => {
    return (
      <>
        {!actionVisible.isVisible && (
          <div className="table-more-content">
            <div
              className="d-flex align-items-center mb-2 flex-row more-item"
              onClick={() =>{
                setActionVisible({
                  isVisible: true,
                  action: MoreActionType.EDIT_DISPLAY_NAME,
                })
                restProps.setFieldValue("displayName", categoryDisplayName)
              }
              }
            >
              <span className="py-1" style={{paddingLeft:2,paddingRight:1}}><BoxIcons type={IconTypes.BOX_ICON} name="rename" /></span>
              <span className="p-1">{t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CHANGE_DISPLAY_NAME")}</span>
            </div>
            <div className="d-flex align-items-center mb-2 flex-row more-item" onClick={() => veiwCategoryDetails()}>
              <EyeOutlined className="p-1"/>
              <span className="p-1">{t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_DETAILS")}</span>
            </div>
          </div>
        )}
        {actionVisible.isVisible && (
          <div>
            {actionVisible.action == MoreActionType.EDIT_DISPLAY_NAME && (
              <div>
                <div className="pb-2">
                  <$Input
                    size="small"
                    label={t(
                      "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DISPLAY_NAME"
                    )}
                    className="w-100"
                    name="displayName"
                    disabled={isDisplayNameUpdating}
                    style={{width:300}}
                  />
                </div>
                <div className="d-flex justify-content-end">
                  <$Button
                    size="small"
                    className="mr-2"
                    htmlType="submit"
                    type="primary"
                    onClick={handleSubmit}
                    disabled={
                      isDisplayNameUpdating ||
                      values?.displayName == categoryDisplayName ||
                      !restProps?.isValid
                    }
                  >
                    {t("US.COMMON:COMMON.SAVE")}
                  </$Button>
                  <$Button
                    size="small"
                    onClick={() =>
                      setActionVisible({ isVisible: false, action: "" })
                    }
                  >
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                </div>
              </div>
            )}
            {actionVisible.action == MoreActionType.VIEW_DETAILS && (
            <div style={{ minWidth: '150px' }}>
              <$Skeleton
                loading={isLoading}
                active
                paragraph={{
                  rows: 1,
                }}
              >
                <div className="dom-amt-list-table pb-3">
                  <div className="list-table-row">
                    <div className="list-table-col">
                      {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DISPLAY_NAME")}
                    </div>
                    <div className="list-table-col px-2">
                      -
                    </div>
                    <div className="list-table-col">
                      {data?.displayName}
                    </div>
                  </div>
                  <div className="list-table-row">
                    <div className="list-table-col">
                      {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.GROUP")}
                    </div>
                    <div className="list-table-col px-2">
                      -
                    </div>
                    <div className="list-table-col">
                      {data?.categoryGroup}
                    </div>
                  </div>
                  <div className="list-table-row">
                    <div className="list-table-col">
                      {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_TYPE")}
                    </div>
                    <div className="list-table-col px-2">
                      -
                    </div>
                    <div className="list-table-col">
                      {data?.adapterType}
                    </div>
                  </div>
                  <div className="list-table-row">
                    <div className="list-table-col">
                      {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADAPTER_SP")}
                    </div>
                    <div className="list-table-col px-2">
                      -
                    </div>
                    <div className="list-table-col">
                      {data?.spName}
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <$Button
                    size="small"
                    onClick={() =>
                      setActionVisible({ isVisible: false, action: "" })
                    }
                  >
                    {t("US.COMMON:COMMON.CANCEL")}
                  </$Button>
                </div>
              </$Skeleton>
            </div>  
            )}
          </div>
        )}
      </>
    );
  };

  /**
   * @description - Title for popover views
   * @param actionType - More action types
   * @returns
   */
  const getPopoverTitle = (actionType: string): string => {
    try {
      switch (actionType) {
        case MoreActionType.EDIT_DISPLAY_NAME:
          return t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.EDIT_DISPLAY_NAME");
        case MoreActionType.VIEW_DETAILS:
          return t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.CATEGORY_DETAILS");
        default:
          return "";
      }
    } catch (error) {
      return "";
    }
  };

  return (
    <Formik
      initialValues={{
        displayName: categoryDisplayName,
      }}
      onSubmit={saveDisplayName}
      enableReinitialize
      validationSchema={ValidationSchema(categories)}
    >
      {({ values, handleSubmit, ...restProps }: any) => (
        <$Popover
          content={content(handleSubmit, restProps, values)}
          title={getPopoverTitle(actionVisible.action)}
          trigger="click"
          placement="right"
          open={categoryId == popVisible}
          onOpenChange ={(visible: boolean) => {
            setPopVisible(visible ? categoryId : -1);
            setActionVisible({
              isVisible: false,
              action: "",
            });
          }}
          overlayClassName="cm-popover"
        >
          <$Button icon={<MoreOutlined />} size="small" type="link" className="cm-btn" />
        </$Popover>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { editorHome } = state;
  const { catgoryDetails, isDisplayNameUpdating } = editorHome;
  return {
    isDisplayNameUpdating,
    catgoryDetails,
  };
};

const { editorHome } = Actions;
const { categoryDisplayName, categoryDetails } = editorHome;

const mapDispatchToProps = {
  updateDisplayName: categoryDisplayName.update,
  getCategoryDetails: categoryDetails.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(MoreOption);
